import { useState, useEffect, useMemo } from 'react'
import { X, Link2 } from 'lucide-react'
import { useAuth } from '../../../../context/AuthContext'

// Multi-select modal for linking ads to a target entity (zone, group,
// advertiser). Loads active ads, filters out already-linked, applies
// the link via POST /sponsors/ads/:id/link per selected ad. Backend
// route accepts kind=zone|group|advertiser uniformly.
//
// Props:
//   kind                       — 'zone' | 'group' | 'advertiser'
//   entityId, entityName       — target entity id + display name (also used
//                                as target_name in the link payload —
//                                AdSpeed token recipe needs it)
//   alreadyLinkedAdIds (Set)   — ids to filter out
//   onClose, onComplete        — callbacks; onComplete(ok, fail, lastErr)
//
// AdSpeed method used per kind (server-side):
//   zone       → AS.Ad.linkToZone       (token: md5([Ad ID][Ad Name][Zone ID][Zone Name]))
//   group      → AS.Ad.linkToGroup      (token: md5([Ad ID][Ad Name][Group ID][Group Name]))
//   advertiser → AS.Ad.linkToAdvertiser (token: md5([Ad ID][Ad Name][Adv ID][Adv USERNAME]))
//
// Note for `advertiser`: entityName should be the advertiser's USERNAME,
// not their fullname or company. AdSpeed's docs are explicit about this.
export default function LinkAdsToEntityModal({
  kind, entityId, entityName, alreadyLinkedAdIds, onClose, onComplete,
}) {
  const { api } = useAuth()
  const [ads, setAds] = useState([])
  const [loading, setLoading] = useState(true)
  const [selected, setSelected] = useState(new Set())
  const [search, setSearch] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    api('/sponsors/ads?status=active')
      .then(d => setAds(d.ads || []))
      .catch(e => setError(`Load error: ${e.message}`))
      .finally(() => setLoading(false))
  }, [api])

  const available = useMemo(() => {
    const q = search.trim().toLowerCase()
    return ads.filter(a => {
      const id = String(a.id ?? a.AdId)
      if (alreadyLinkedAdIds.has(id)) return false
      if (!q) return true
      const name = String(a.name ?? a.Name ?? '').toLowerCase()
      return name.includes(q) || id.includes(q)
    })
  }, [ads, search, alreadyLinkedAdIds])

  function toggle(id) {
    setSelected(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }
  function toggleAll() {
    const allIds = available.map(a => String(a.id ?? a.AdId))
    if (allIds.every(id => selected.has(id))) setSelected(new Set())
    else setSelected(new Set(allIds))
  }

  // AdSpeed method name per kind, for the quota-warning copy.
  const methodLabel = {
    zone:       'AS.Ad.linkToZone',
    group:      'AS.Ad.linkToGroup',
    advertiser: 'AS.Ad.linkToAdvertiser',
  }[kind] || 'AS.Ad.link*'

  async function handleLink() {
    if (selected.size === 0) {
      setError('Pick at least one ad.')
      return
    }
    if (!confirm(
      `Link ${selected.size} ad(s) to ${kind} "${entityName}"?\n\n` +
      `Uses ${selected.size} ${methodLabel} calls (limit 50/day).`
    )) return

    setSaving(true)
    setError('')
    let ok = 0, fail = 0, lastErr = ''
    for (const adId of selected) {
      try {
        const res = await api(`/sponsors/ads/${adId}/link`, {
          method: 'POST',
          body: JSON.stringify({ kind, target_id: entityId, target_name: entityName }),
        })
        if (res?.ok === false) throw new Error(res.error?.message || 'AdSpeed rejected')
        ok++
      } catch (e) {
        fail++
        lastErr = e.message
      }
    }
    setSaving(false)
    onComplete(ok, fail, lastErr)
  }

  const allVisibleSelected = available.length > 0
    && available.every(a => selected.has(String(a.id ?? a.AdId)))

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: '#121212', border: '1px solid #333', borderRadius: 10,
          padding: 24, minWidth: 560, maxWidth: 720, width: '90%',
          maxHeight: '85vh', display: 'flex', flexDirection: 'column',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
          <h2 style={{ margin: 0, fontSize: '1.2rem' }}>
            Link ads to {kind} <span style={{ color: '#f59e0b' }}>{entityName}</span>
          </h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#aaa', cursor: 'pointer' }}>
            <X size={18} />
          </button>
        </div>

        <input
          className="zones-search"
          placeholder="Filter by name or id…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ marginBottom: 10 }}
        />

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <span className="muted" style={{ fontSize: '0.85rem' }}>
            {loading ? 'Loading…' : `${available.length} available · ${selected.size} selected`}
          </span>
          <button
            type="button"
            className="sponsors-refresh-btn"
            onClick={toggleAll}
            disabled={loading || available.length === 0}
            style={{ fontSize: '0.78rem' }}
          >
            {allVisibleSelected ? 'Deselect all' : 'Select all visible'}
          </button>
        </div>

        <div style={{
          flex: 1, overflowY: 'auto', minHeight: 200, maxHeight: 400,
          border: '1px solid #333', borderRadius: 6, background: '#0e0e0e',
        }}>
          {loading ? (
            <div className="muted" style={{ padding: 16 }}>Loading…</div>
          ) : available.length === 0 ? (
            <div className="muted" style={{ padding: 16 }}>
              {ads.length === 0
                ? 'No active ads.'
                : `All active ads are already linked to this ${kind} (or filtered out).`}
            </div>
          ) : (
            <table className="zones-table" style={{ width: '100%' }}>
              <tbody>
                {available.map(a => {
                  const id = String(a.id ?? a.AdId)
                  const w = a.width ?? a.Width
                  const h = a.height ?? a.Height
                  const isSel = selected.has(id)
                  return (
                    <tr key={id} onClick={() => toggle(id)} style={{ cursor: 'pointer', background: isSel ? '#1e3a8a30' : undefined }}>
                      <td style={{ width: 32 }}>
                        <input type="checkbox" checked={isSel} onChange={() => toggle(id)} />
                      </td>
                      <td>{id}</td>
                      <td>{a.name ?? a.Name}</td>
                      <td>{w && h ? `${w}x${h}` : ''}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          )}
        </div>

        {error && (
          <div className="sponsors-status error" style={{ marginTop: 10, marginBottom: 0 }}>{error}</div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
          <button type="button" onClick={onClose} className="sponsors-refresh-btn">
            Cancel
          </button>
          <button
            type="button"
            onClick={handleLink}
            disabled={saving || selected.size === 0}
            className="btn btn-primary"
          >
            <Link2 size={13} /> {saving ? 'Linking…' : `Link ${selected.size} ad${selected.size === 1 ? '' : 's'}`}
          </button>
        </div>
      </div>
    </div>
  )
}
